import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setCountry } from "../../features/map/mapSlice";
import MapDTO from "../../shared/dtos/mapDTO";
import providerDTO from "../../shared/dtos/providerDTO";
import { RootState } from "../../app/store";
import { ProviderMarkers } from "./ProviderMarkers";
import { COUNTRIES } from "../../utils/constants/countries";
import { WayPoints } from "./Waypoints";
import CheckPoints from "./CheckPoints";
import {
  subscribeTripTracking,
  unSubscribe,
} from "../../utils/externals/firebase/firebaseDB";
import { DatabaseReference } from "firebase/database";
import { LocationDTO } from "../../shared/dtos/PassengerGroupDTO";
import { DARK_STYLES } from "../../shared/constants/DarkStyles";

interface MapProps {
  destinations?: MapDTO | null;
  providers?: Array<providerDTO>;
  showProviders?: boolean | false;
  assignProvider?: (provider: providerDTO) => void;
  setProvider?: (provider: providerDTO | null) => void;
  selectedProvider?: providerDTO | null;
  activeProvider?: providerDTO | null;
  shouldCalculateDistance?: boolean | false;
  bookingId?: string | null;
  height?: string;
}

let tripTrackingRef: DatabaseReference | null = null;

export const DispatcherMap: React.FunctionComponent<MapProps> = (props) => {
  const {
    destinations,
    providers,
    showProviders,
    assignProvider,
    selectedProvider,
    setProvider,
    activeProvider,
    shouldCalculateDistance,
    bookingId,
  } = props;
  const dispatch = useAppDispatch();
  const darkMode: boolean = useAppSelector(
    (state: RootState) => state.currentAdmin.value.darkMode,
  );
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [providerPosition, setProviderPosition] = useState<LocationDTO | null>(
    null,
  );

  useEffect(() => {
    dispatch(setCountry(COUNTRIES[0]));
  }, [dispatch]);

  const { lat, lng } = useAppSelector((state: RootState) => state?.map.country);

  const center = useMemo(() => {
    return { lat: -34.88582, lng: -56.17122 };
  }, []);

  useEffect(() => {
    setProviderPosition(null);
    if (tripTrackingRef) unSubscribe(tripTrackingRef);

    try {
      if (activeProvider && bookingId)
        tripTrackingRef = subscribeTripTracking(bookingId, setProviderPosition);
    } catch (e) {
      setProviderPosition(null);
    }
  }, [activeProvider, bookingId]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);
      map.setCenter(center);
      map.setZoom(12);
    },
    [center],
  );

  const wayPointsDirections = useMemo(() => {
    return {
      pickUp: destinations?.pickUp,
      dropOff: destinations?.dropOff,
    };
  }, [destinations]);

  useEffect(() => {
    const origin = destinations?.pickUp;
    if (map && origin) {
      map.setCenter({
        lat: origin.originLatitude,
        lng: origin.originLongitude,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinations?.pickUp]);

  return (
    <GoogleMap
      mapContainerStyle={{
        height: props.height ?? "80vh",
      }}
      onLoad={onLoad}
      options={darkMode ? DARK_STYLES : { styles: [] }}
      center={{ lat, lng }}
    >
      {destinations && destinations.wayPoints ? (
        <CheckPoints destinations={destinations} />
      ) : (
        destinations &&
        destinations.pickUp && (
          <WayPoints
            directions={wayPointsDirections}
            shouldCalculateDistance={shouldCalculateDistance}
          />
        )
      )}

      {showProviders && providers && !activeProvider && (
        <ProviderMarkers
          providers={providers}
          assignProvider={assignProvider}
          selectedProvider={selectedProvider}
          setProvider={setProvider}
          map={map}
        />
      )}
      {activeProvider && providerPosition && (
        <ProviderMarkers
          providers={[
            {
              ...activeProvider,
              longitude: providerPosition.longitude,
              latitude: providerPosition.latitude,
            },
          ]}
          selectedProvider={{
            ...activeProvider,
            longitude: providerPosition.longitude,
            latitude: providerPosition.latitude,
          }}
          map={map}
        />
      )}
    </GoogleMap>
  );
};
